import { apiRequest } from '@/utils/apiRequest'
import { apiUrl, extractCollection } from '@/utils/apiUrl'
import { withCurrentStaff } from '@/utils/withCurrentStaff'

export class ReservationRepository {
	constructor() {
		const host = window.location.hostname
		if (host === 'localhost' || host.startsWith('192.168')) {
			this.urlBase = process.env.VUE_APP_API_BASE_URL
		} else {
			this.urlBase = window.location.origin
		}
	}

	async find(reservationId) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/reservationinfo'), method: 'POST', body: { reservationId }, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal obteniendo la informacion de la reserva.')
		}
	}

	async create(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/reservation/register'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal creando la reserva.')
		}
	}

	async update({ client, data }) {
		const url = `https://${client}.wewelcom.io/world/api/reservation/updatereservation`
		try {
			const response = await apiRequest({ url, method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal actualizando la reserva.')
		}
	}

	async getAvailableRooms() {
		try {
			const response = await apiRequest({ url: apiUrl('/world/reservation/availablerooms'), method: 'POST', body: {}, throwOnNonOk: true })
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal obteniendo las aviableRooms.')
		}
	}

	async checkAvailability(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/reservation/checkavailability'), method: 'POST', body: data, throwOnNonOk: true })
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal comprobando la disponibilidad.')
		}
	}

	async cancelReservation(reservationId) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/reservation/canceldirect'), method: 'POST', body: { reservationId }, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal terminando reserva por email')
		}
	}

	async cancelAssistanceReservation(reservationId, userId) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/reservation/removeinvitee'), method: 'POST', body: { reservationId, userId }, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal terminando la cancelacion de la asistencia')
		}
	}

	async addInvitedUser(reservationWid, userId) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/adduser'), method: 'POST', body: { reservationWid, userId }, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal aceptando la reserva por invitacion.')
		}
	}

	async addUnknownInvitedUser(reservationWid, hostCode, invitedUserName) {
		try {
			const response = await apiRequest({
				url: apiUrl('/world/api/reservation/addguest'),
				method: 'POST',
				body: { createGuest: true, reservationWid, hostCode, invitedUserName },
				throwOnNonOk: false,
			})
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal aceptando la reserva por invitacion de usuario desconocido.')
		}
	}

	async getReservationsByDay(date) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/getbyday'), method: 'POST', body: { date }, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal pidiendo la informacion de reservas')
		}
	}

	async getReservationsByScope(date, scope = null) {
		const params = new URLSearchParams()
		if (date && date !== 'null' && date !== 'undefined') {
			params.set('date', date)
		}
		if (scope) params.set('scope', scope)
		const query = params.toString()
		const url = apiUrl(`/world/api/reservations${query ? `?${query}` : ''}`)
		try {
			const response = await apiRequest({ url, method: 'GET', expect: 'json' })
			const data = response?.data ?? response
			return extractCollection(data)
		} catch (e) {
			console.error('Error fetching reservations with scope:', scope, e)
			return []
		}
	}

	async getTakeAway() {
		try {
			const response = await apiRequest({ url: apiUrl('/world/account/gettakeaway'), method: 'POST', body: {}, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal en el getTakeAway')
		}
	}

	async setNoShow(reservationWid) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/set-no-show'), method: 'POST', body: { reservationWid }, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal finalizando la reserva.')
		}
	}

	async setPaymentPending(reservationWid, paymentMethod, uid = null, roleId = null, staffId = null) {
		try {
			const body = { reservationWid, paymentMethod, uid, roleId }
			if (staffId) {
				body.staffId = staffId
			}
			const response = await apiRequest({
				url: apiUrl('/world/api/reservation/request-ticket'),
				method: 'POST',
				body,
				throwOnNonOk: false,
			})
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal finalizando la reserva.')
		}
	}

	async reactivateReservation(reservationWid) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/reactivate'), method: 'POST', body: { reservationWid }, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal reactivate la reserva.')
		}
	}

	async updatePax(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/updatepax'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal modificando los pax')
		}
	}

	async createPayment(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/payment/create'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Algo ha salido mal en createPayment')
		}
	}

	async getPaymentInfo(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/payment-info'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Algo ha salido mal en getPaymentInfo')
		}
	}

	async getBillPaymentInfo(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/bill-payment-info'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Algo ha salido mal en getPaymentInfo')
		}
	}

	async getBillPrintInfo(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/bill/print-info'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Algo ha salido mal en getBillPrintInfo')
		}
	}

	async getTipPrintInfo(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/tip/print-info'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Algo ha salido mal en getTipPrintInfo')
		}
	}

	async enableReservation(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/enable'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal habilitando la reserva.')
		}
	}

	async disableReservation(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/disable'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal deshabilitando la reserva.')
		}
	}

	async unifyReservations(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/reservation/merge'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal unificando las reservas.')
		}
	}

	async markProductOrdersPaid(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/productorder/paid'), method: 'POST', body: withCurrentStaff(data), throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal marcando productos como pagados.')
		}
	}

	async markProductOrdersUnpaid(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/productorder/unpaid'), method: 'POST', body: withCurrentStaff(data), throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal marcando productos como no pagados.')
		}
	}

	async emitProductOrdersInvoice(productOrderIds) {
		try {
			const response = await apiRequest({
				url: apiUrl('/world/api/productorder/emit-invoice'),
				method: 'POST',
				body: withCurrentStaff({ productOrderIds }),
				throwOnNonOk: false,
			})
			if (!response.ok) return { result: -1, message: response.status }
			return response.data
		} catch (e) {
			console.log('Error emitiendo factura simplificada.', e)
			return { result: -1, message: 'Error de red' }
		}
	}

	async rectifyFiscalDocument(fiscalDocumentId, rectificationType, reason) {
		try {
			const response = await apiRequest({
				url: apiUrl(`/world/api/fiscal-documents/${fiscalDocumentId}/rectify`),
				method: 'POST',
				body: { rectificationType, reason },
				throwOnNonOk: false,
			})
			if (!response.ok) return { result: -1, message: response.status }
			return { result: 0, document: response.data }
		} catch (e) {
			console.log('Error rectificando factura.', e)
			return { result: -1, message: 'Error de red' }
		}
	}

	/**
	 * Issues a Rectificativa por sustitución to correct formal customer data
	 * (name/NIF/address) on an already-issued nominative simplified invoice.
	 * @param {string} fiscalDocumentId
	 * @param {{ customerName: string, customerNif: string, customerAddress?: string, reason: string }} payload
	 */
	async correctCustomerOnFiscalDocument(fiscalDocumentId, payload) {
		try {
			const response = await apiRequest({
				url: apiUrl(`/world/api/fiscal-documents/${fiscalDocumentId}/correct-customer`),
				method: 'POST',
				body: payload,
				throwOnNonOk: false,
			})
			if (!response.ok) {
				const detail = response.data?.detail ?? response.data?.message ?? response.status
				return { result: -1, message: detail }
			}
			return { result: 0, document: response.data }
		} catch (e) {
			console.log('Error corrigiendo datos de cliente en factura.', e)
			return { result: -1, message: 'Error de red' }
		}
	}

	async moveProductsOrdersReservation(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/productorder/modify-reservation'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal moviendo productos de reserva.')
		}
	}

	async moveProductsOrdersNewReservation(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/reservation/createwalkin'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal moviendo los productos.')
		}
	}

	async updateLocationTag(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/reservation/update-location-tag'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal actualizando el location tag.')
		}
	}

	async addInternalCommentReservation(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/update-staff-comments'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal actualizando los comentarios.')
		}
	}

	async updateMethodPaymentReservation(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/payment/update-method'), method: 'POST', body: data, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal actualizando el método de pago.')
		}
	}

	async markAsTest(reservationId) {
		try {
			const response = await apiRequest({
				url: apiUrl(`/world/api/reservation/${reservationId}/mark-as-test`),
				method: 'POST',
				body: withCurrentStaff({}),
				throwOnNonOk: false,
			})
			if (!response.ok) {
				return { result: -1, message: response.data?.message ?? 'No se ha podido marcar como prueba', status: response.status }
			}
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal marcando la reserva como test.', e)
			return { result: -1, message: 'Error de comunicación' }
		}
	}

	async paidReservation(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/paid-reservation'), method: 'POST', body: withCurrentStaff(data), throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal actualizando el paidReservation.')
		}
	}

	async emitSimplifiedInvoice(reservationId) {
		const response = await apiRequest({
			url: apiUrl('/world/api/reservation/emit-simplified-invoice'),
			method: 'POST',
			body: withCurrentStaff({ reservationId }),
			throwOnNonOk: false,
		})
		if (!response.ok) throw new Error(response.data?.message ?? 'Error al emitir factura')
		return response.data
	}

	async reservationReadyToOrder(reservationId) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/reservation/ready-to-order'), method: 'POST', body: { reservationId }, throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (e) {
			console.log('Ups, algo ha ido mal actualizando el reservationReadyToOrder.')
		}
	}

	async viewBillPdf(reservationId) {
		try {
			const response = await apiRequest({
				url: apiUrl('/world/api/bill/view-bill-pdf'),
				method: 'POST',
				body: { billId: reservationId },
				expect: 'blob',
				headers: { Accept: 'application/pdf' },
				throwOnNonOk: true,
			})
			return response.data
		} catch (error) {
			console.error('Error fetching the bill PDF:', error)
			throw error
		}
	}

	async viewTipPdf(tipId) {
		try {
			const response = await apiRequest({
				url: apiUrl(`/world/api/tip/${tipId}/preview`),
				method: 'GET',
				expect: 'blob',
				headers: { Accept: 'application/pdf' },
				throwOnNonOk: true,
			})
			return response.data
		} catch (error) {
			console.error('Error fetching the tip PDF:', error)
			throw error
		}
	}

	async viewFiscalDocumentPdf(fiscalDocumentId) {
		try {
			const response = await apiRequest({
				url: apiUrl(`/world/api/fiscal-documents/${fiscalDocumentId}/pdf`),
				method: 'GET',
				expect: 'blob',
				headers: { Accept: 'application/pdf' },
				throwOnNonOk: true,
			})
			return response.data
		} catch (error) {
			console.error('Error fetching fiscal document PDF:', error)
			throw error
		}
	}

	async createTipToReservation(data) {
		try {
			const response = await apiRequest({ url: apiUrl('/world/api/tip/create'), method: 'POST', body: withCurrentStaff(data), throwOnNonOk: false })
			if (!response.ok) return response.status
			return response.data
		} catch (error) {
			console.error('Error fetching the createTipToReservation:', error)
			throw error
		}
	}

	async closeReservation(reservationWid) {
		try {
			const { data } = await apiRequest({
				url: apiUrl(`/world/api/reservation/${reservationWid}/close`),
				method: 'POST',
				body: null,
				expect: 'json',
				throwOnNonOk: true
			})

			return data
		} catch (e) {
			console.log('Error closing reservation:', e)
			throw e
		}
	}

	/**
	 * Update delivery reservation status.
	 * @param {string} reservationId - The reservation UUID (realId)
	 * @param {number} deliveryStatus - The target delivery status code
	 * @returns {Promise<{deliveryStatus: number, deliveryStatusLabel: string, reservationId: string}>}
	 */
	async updateDeliveryStatus(reservationId, deliveryStatus) {
		try {
			const { data } = await apiRequest({
				url: apiUrl('/world/api/delivery/reservations/status'),
				method: 'POST',
				body: { reservationId, deliveryStatus },
				expect: 'json',
				throwOnNonOk: true
			})

			return data
		} catch (e) {
			console.error('Error updating delivery status:', e)
			throw e
		}
	}

	/**
	 * Update takeaway reservation preparation status.
	 * @param {string} reservationId - The reservation UUID (realId)
	 * @param {number} preparationStatus - The target preparation status code
	 * @returns {Promise<{preparationStatus: number, preparationStatusLabel: string, reservationId: string}>}
	 */
	async updateTakeawayStatus(reservationId, preparationStatus) {
		try {
			const { data } = await apiRequest({
				url: apiUrl('/world/api/takeaway/reservations/status'),
				method: 'POST',
				body: { reservationId, preparationStatus },
				expect: 'json',
				throwOnNonOk: true
			})

			return data
		} catch (e) {
			console.error('Error updating takeaway status:', e)
			throw e
		}
	}
}

export const reservationRepository = new ReservationRepository()
